import { Card, Flex, Popconfirm, Spin } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { BaseButton } from 'components/button/button-base';
import { Text } from 'components/mvp-typography';
import { QAPanelTemplate } from 'features/patient/assessment/assessment-controller/_components/questionnaire-qa-panel';
import { useAnalytics } from 'lib/hooks/use-analytics';
import useSearchParams from 'lib/hooks/use-searchParams';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes';
import { FormPatientAdd } from './_components/form-patient-add';
import { IntegrationImpactLayout } from './_components/layout';
import {
  useDecodeToken,
  useImpactPilotDecodeAndCheckUseOfToken
} from './_utils/use-impact-pilot-mutations';
import { DEBUGTokenStates } from './page-patient-add_debug';
import { PageIntegrationPilotContainer } from './page-pilot-container';

export function PageIntegrationIMPACTPatientAdd() {
  return (
    <IntegrationImpactLayout>
      <Internal_PageIntegrationIMPACTPatientAdd />
    </IntegrationImpactLayout>
  );
}

function Internal_PageIntegrationIMPACTPatientAdd() {
  const analytics = useAnalytics();
  const [params, setSearchParams] = useSearchParams();
  const history = useHistory();

  const userToken: string | null = params.get('token');
  const locationToken: string | null = params.get('location');
  const decodeTokenQuery = useImpactPilotDecodeAndCheckUseOfToken(
    userToken,
    locationToken
  );
  const decodeLocationTokenQuery = useDecodeToken(locationToken);

  // Automatically redirect if passport is already assigned to the patient
  useEffect(
    () => {
      if (
        decodeTokenQuery.isSuccess &&
        decodeTokenQuery.data.assignmentDetails.status === true
      ) {
        analytics.track(
          'integration:impact:pilot:onboarding:add:already-added'
        );
        history.push(
          paths.clinician.patient.assessments(
            decodeTokenQuery.data.assignmentDetails.patientId
          )
        );
      }
    },
    // eslint-disable-next-line
    [
      decodeTokenQuery.isSuccess,
      decodeTokenQuery.data,
      history,
      analytics.track
    ]
  );

  return (
    <PageIntegrationPilotContainer step={0}>
      <Flex vertical align="center" justify="center" style={{ height: '100%' }}>
        <Card style={{ width: '70%' }}>
          <Flex vertical gap={16}>
            <QAPanelTemplate>
              <Flex vertical gap={16}>
                <Text>Token from ImPACT: {userToken}</Text>
                <DEBUGTokenStates
                  setSearchParams={setSearchParams}
                  token={userToken}
                />
              </Flex>
            </QAPanelTemplate>

            {decodeTokenQuery.isFetching && (
              <Flex
                justify="center"
                align="center"
                style={{ height: 100 }}
                vertical
              >
                <Spin size="large" tip="Decrypting token..." />
                <Text>
                  <small>Fetching details...</small>
                </Text>
              </Flex>
            )}

            {decodeTokenQuery.isError && (
              <Flex vertical gap={16} align="center">
                <ErrorDisplay
                  error={resolveError(decodeTokenQuery.error)}
                  title="Token decryption error"
                  description={
                    <Text>
                      Please report this error to{' '}
                      <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>
                        {CRX_SUPPORT_EMAIL}
                      </a>
                    </Text>
                  }
                />
                <BaseButton onClick={() => setSearchParams({ token: null })}>
                  Enter a Passport ID manually
                </BaseButton>
                <BaseButton onClick={() => decodeTokenQuery.refetch()}>
                  Try Again
                </BaseButton>
              </Flex>
            )}
            {(decodeTokenQuery.isSuccess || userToken === null) && (
              <FormPatientAdd
                passportId={decodeTokenQuery.data?.passportId ?? ''}
                locationCode={decodeLocationTokenQuery.data}
                onSuccess={result => {
                  analytics.track(
                    'integration:impact:pilot:onboarding:add:success'
                  );
                  return setTimeout(() => {
                    history.push(
                      paths.clinician.patient.assessments(result.data.patientId)
                    );
                  }, 1000);
                }}
                title={{
                  content: 'Add a test-taker'
                }}
                instructionText={{
                  content: `If you would like to start by adding your ImPACT test-taker before
              exploring the app, please insert their Passport ID below.`
                }}
                formInputs={{
                  passportId: {
                    disabled: decodeTokenQuery.data?.passportId !== undefined
                  },
                  locationCode: {
                    disabled: decodeLocationTokenQuery.data !== undefined
                  }
                }}
                renderCancel={() => (
                  <Popconfirm
                    title="Before you go"
                    description="You can access this screen again from your home page."
                    onConfirm={() => {
                      analytics.track(
                        'integration:impact:pilot:onboarding:add:skip'
                      );
                      return history.push('/');
                    }}
                  >
                    <BaseButton type="text">Not right now</BaseButton>
                  </Popconfirm>
                )}
                renderFooter={status => {
                  if (status.isSuccess) {
                    return (
                      <Text type="success">
                        Test-taker added successfully. Redirecting...
                      </Text>
                    );
                  }

                  return null;
                }}
              />
            )}
          </Flex>
        </Card>
      </Flex>
    </PageIntegrationPilotContainer>
  );
}
